import React, { useState, useContext, useEffect } from 'react';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { onGlobalError } from '@loyalup/core';

import Notification from '../Notification';

import s from './Notifications.module.css';

const NotificationContext = React.createContext({});

let ID = 0;
let __notifications = [];

const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const updateNotifications = notifications => {
    setNotifications(notifications);
    __notifications = notifications;
  };

  const removeNotification = id => {
    updateNotifications(__notifications.filter(n => n.id !== id));
  };

  const addNotification = ({ text, type, timeout }) => {
    const id = ID++;
    const notification = {
      id,
      text,
      type,
      key: id,
      expires: new Date(Date.now() + (timeout || 3000)),
      onClose: () => removeNotification(id),
    };
    updateNotifications([notification].concat(notifications));
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      const notifications = __notifications.filter(n => n.expires > now);
      if (notifications.length < __notifications.length) updateNotifications(notifications);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <NotificationContext.Provider value={[addNotification, notifications]}>{children}</NotificationContext.Provider>
  );
};

const useNotifications = () => useContext(NotificationContext);

const NotificationCenter = () => {
  const [t] = useTranslation();
  const [sendNotification, notifications] = useNotifications();
  const [empty, setEmpty] = useState(true);

  useEffect(
    () =>
      onGlobalError(() => {
        sendNotification({ text: t('Ошибка сервера'), type: 'error' });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (notifications.length) {
      setEmpty(false);
      return;
    }

    const handler = setTimeout(() => setEmpty(notifications.length === 0), 3000);
    return () => {
      clearTimeout(handler);
    };
  }, [notifications.length]);

  return (
    <CSSTransitionGroup
      component="div"
      className={cn(s.root, { [s.empty]: empty })}
      transitionName={{
        enter: s.enter,
        enterActive: s.enterActive,
        leave: s.leave,
        leaveActive: s.leaveActive,
      }}
      transitionEnterTimeout={300}
      transitionLeaveTimeout={150}
    >
      {notifications.map(notification => (
        <Notification {...notification} />
      ))}
    </CSSTransitionGroup>
  );
};

export { useNotifications, NotificationsProvider, NotificationCenter };
